/**
 * provenanceSource — resolve a twin transition's raw ``source_id`` into
 * something a farmer can read (#2791).
 *
 * ``twin_attribute_transitions.source_id`` is OVERLOADED. The backend
 * documents it as "document provenance where the recorder had one"
 * (``backend/src/api/v1/endpoints/twin/history.py``), and the document
 * lane does write a real ``documents.document_id`` there — but the five
 * structured backfill lanes stamp a synthetic SOURCE-SYSTEM marker
 * instead (``wiberry-mysql-backfill``, …). Both shapes reach the UI on the
 * same field, so the grid cannot print the value and cannot assume either
 * meaning: it has to ask which one this is.
 *
 * Three outcomes, and the third one is the point:
 *
 * - ``document`` — the id resolves to one of the entity's LINKED source
 *   docs, so the existing "Beleg" preview thread applies. Resolution goes
 *   through ``provenanceLabel`` so the label and the "no linked doc, no
 *   Beleg" rule stay identical to the Verlauf panel's (#2076).
 * - ``system`` — the token belongs to a known backfill family; render its
 *   collapsed badge. The bank lane mints one token per institution ×
 *   account × month (~150 distinct in WF prod), so raw-token rendering is
 *   not an option — the whole family reads "Bank".
 *
 *   Reach differs per lane today, and the table is deliberately wider than
 *   what is currently observable. myfarm24, wiberry and fruitforecast pass
 *   ``source_id=`` on the upsert, so their tokens do reach transitions.
 *   ``backfill_bank.py`` and the wibill lane do NOT (neither file mentions
 *   ``source_id`` at all) — their token lands only in the entity-level
 *   ``source_doc_ids`` attribute, so those two branches are reachable via
 *   that shape and forward-compatible with the writers gaining ``source_id=``
 *   like their three siblings. Keeping them costs one regex each and avoids a
 *   silent no-badge hole the day those writers are fixed.
 *   Since #3385 the family table is the SERVER registry
 *   (``config/source-systems.yaml`` via
 *   ``GET /v1/twin/provenance/source-systems``), threaded in as the third
 *   argument; ``DEFAULT_SOURCE_SYSTEMS`` below is today's five-entry table
 *   verbatim and serves every call that does not thread one — the pre-fetch
 *   paint, the bare-render tests, and a registry outage alike. Adding a
 *   source system is therefore a config edit, not a frontend release.
 * - ``unknown`` — render NOTHING. Never a fallback badge, never a raw
 *   token, never the entity-level ``source_doc_ids`` union (which is wrong
 *   precisely on the converged multi-source entities this feature exists
 *   for). A pre-recorder entity or a token we do not know is a supported
 *   state, not an error.
 */
import type { EntitySourceDoc, SourceSystemEntry } from '../../../services/twinService';
import { provenanceLabel } from './attributeHistory';

/** Re-exported so every provenance consumer imports one module, not two.
 * The wire shape itself lives beside the fetch, in ``twinService``. */
export type { SourceSystemEntry };

export type ProvenanceKind = 'document' | 'system' | 'unknown';

export interface ResolvedProvenance {
  kind: ProvenanceKind;
  /** Human label ("Beleg 2024", "myfarm24"); '' when kind is 'unknown'. */
  label: string;
  /**
   * The id safe to hand to ``onPreviewDocument`` — non-null ONLY for kind
   * ``document``. A system token that reached the preview thread could
   * never resolve to a document, which is the bug this field closes.
   */
  documentId: string | null;
}

/** Frozen: a single shared instance is returned by reference to every caller,
 * so an accidental mutation would poison every future unknown result. */
const UNKNOWN: ResolvedProvenance = Object.freeze({ kind: 'unknown', label: '', documentId: null });

/** One compiled registry row: the family matcher plus the label it renders. */
export interface CompiledSourceSystem {
  readonly match: RegExp;
  readonly label: string;
}

/**
 * The table this module used to freeze into the bundle — now the OFFLINE
 * FALLBACK only (#3385 subtask 2).
 *
 * It is deliberately today's five entries **verbatim**, not the server
 * registry's seven: reverting this subtask has to return the grid to exactly
 * the behaviour it shipped with, while the backend registry, its endpoint and
 * the ``source_system`` stamping keep working for operators. Widening the
 * defaults would quietly make the revert a behaviour change of its own.
 *
 * Note ``bank``: its only token family is ``bank-backfill``, because that is
 * the only family today's table matched. The server registry additionally
 * claims the bare ``bank`` token — a widening that arrives through the fetch,
 * not through this constant.
 *
 * Matching is deliberately lenient on the SUFFIX and strict on the PREFIX. The
 * suffix varies by writer — the transition token is bare
 * (``myfarm24-csv-backfill``) while the entity-level one carries the import
 * year (``myfarm24-csv-backfill-2027``), and the bank lane appends
 * ``:<institution>:<account>:<M/YYYY>`` — so anchoring the family prefix and
 * ignoring what follows covers both of today's shapes and any future
 * qualifier. The boundary is what keeps it honest: ``myfarm240-…`` and
 * ``wiberryfarm-export`` are NOT these families and must fall through to
 * "no badge" rather than borrowing a neighbour's label.
 */
export const DEFAULT_SOURCE_SYSTEMS: ReadonlyArray<SourceSystemEntry> = Object.freeze([
  Object.freeze({ id: 'bank', display_name: 'Bank', token_prefixes: Object.freeze(['bank-backfill']) }),
  Object.freeze({ id: 'myfarm24', display_name: 'myfarm24' }),
  Object.freeze({ id: 'wiberry', display_name: 'wiberry' }),
  Object.freeze({ id: 'wibill', display_name: 'wibill' }),
  Object.freeze({ id: 'fruitforecast', display_name: 'FruitForecast' }),
]);

/** Escape every RegExp metacharacter — a registry id is a LITERAL prefix.
 * Without this a hypothetical ``a.c`` id would also claim ``abc``. */
function escapeRegExp(literal: string): string {
  return literal.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Compile a registry payload into ordered family matchers.
 *
 * - An ABSENT ``token_prefixes`` defaults to ``[id]`` — the common case, where
 *   the writer's token family and the canonical id coincide. An explicitly
 *   EMPTY list is honoured as "this system claims no families" rather than
 *   silently re-inflated to ``[id]``, so the reader's semantics match the wire
 *   exactly. (The backend rejects an empty list today, so neither branch is
 *   reachable through the live stack — but the two must not disagree.)
 * - Each prefix becomes ``^<escaped>(?=$|[:-])``, case-insensitive, so
 *   ``MyFarm24-CSV-Backfill`` and ``myfarm24-csv-backfill`` are one family.
 *   Prefixes are TRIMMED before compiling: ``" bank"`` would otherwise compile
 *   to a matcher no real token can ever satisfy — a silently dead badge.
 * - Sorted by DESCENDING prefix length (ties broken lexicographically) so a
 *   longer prefix always wins over a shorter overlapping one — ``bank`` and
 *   ``bank-backfill`` both match ``bank-backfill:…``, and which label the
 *   farmer sees must not depend on YAML ordering.
 * - ⚠️ EVERY field is type-checked, not merely null-checked, and a bad row is
 *   SKIPPED rather than thrown on. This function runs inside ``PropertyGrid``'s
 *   render, and the app mounts no ErrorBoundary: a ``TypeError`` here would
 *   white-screen the whole object view. A malformed row must cost one badge,
 *   never the grid. (``?.`` guards nullish but not a wrong TYPE, which is why
 *   ``display_name: 42`` used to throw on ``.trim()``.)
 */
export function buildSourceSystems(
  entries: ReadonlyArray<SourceSystemEntry> | null | undefined,
): ReadonlyArray<CompiledSourceSystem> {
  const rows: Array<{ prefix: string; match: RegExp; label: string }> = [];
  // Not `entries ?? []`: a non-array payload (object, string) is not iterable
  // and would throw on the for-of rather than degrade.
  for (const entry of Array.isArray(entries) ? entries : []) {
    if (!entry || typeof entry.id !== 'string') continue;
    const declared = Array.isArray(entry.token_prefixes) ? entry.token_prefixes : undefined;
    // A string `token_prefixes` is iterable BY CHARACTER — unguarded, "bank"
    // would compile four one-letter matchers.
    const prefixes = declared ?? [entry.id];
    const named = typeof entry.display_name === 'string' ? entry.display_name.trim() : '';
    const label = named || entry.id.trim();
    if (!label) continue;
    for (const raw of prefixes) {
      if (typeof raw !== 'string') continue;
      const prefix = raw.trim();
      if (prefix === '') continue;
      rows.push({ prefix, match: new RegExp(`^${escapeRegExp(prefix)}(?=$|[:-])`, 'i'), label });
    }
  }
  rows.sort((a, b) => b.prefix.length - a.prefix.length || a.prefix.localeCompare(b.prefix));
  return rows.map(({ match, label }) => ({ match, label }));
}

/**
 * Compile once per registry ARRAY IDENTITY.
 *
 * ``resolveProvenanceSource`` is called per grid row (and per alias), so
 * compiling ~10 regexes on every call would be pure waste. Both real callers
 * hand over a stable reference — the module-level ``DEFAULT_SOURCE_SYSTEMS``,
 * or React Query's cached response object — so identity is the right key, and
 * a ``WeakMap`` keeps a discarded payload collectable.
 *
 * ⚠️ The key array is therefore treated as IMMUTABLE: mutating one in place
 * after it has been resolved once leaves the change invisible for the rest of
 * the session. Neither real caller can — ``DEFAULT_SOURCE_SYSTEMS`` is frozen,
 * and React Query's structural sharing REPLACES the array rather than editing
 * it — but ``ReadonlyArray`` is a view, not a guarantee, so a future caller
 * handing over a mutable array must hand over a NEW one to be seen.
 */
const COMPILED_CACHE = new WeakMap<object, ReadonlyArray<CompiledSourceSystem>>();

const NO_SYSTEMS: ReadonlyArray<CompiledSourceSystem> = Object.freeze([]);

function compiledFor(systems: ReadonlyArray<SourceSystemEntry>): ReadonlyArray<CompiledSourceSystem> {
  // `WeakMap.set` throws on a primitive key, so a non-object payload must not
  // reach the cache at all — see buildSourceSystems' render-safety note.
  if (systems === null || typeof systems !== 'object') return NO_SYSTEMS;
  const cached = COMPILED_CACHE.get(systems);
  if (cached) return cached;
  const built = buildSourceSystems(systems);
  COMPILED_CACHE.set(systems, built);
  return built;
}

/**
 * Resolve one attribute's governing ``source_id`` for display.
 *
 * ``docsById`` is the entity's LINKED source documents — the same map the
 * Belege panel and the Verlauf log trust. A document id absent from it is
 * unresolvable here too, so it degrades to ``unknown`` rather than
 * rendering a Beleg chip the farmer could click but never open.
 *
 * ``systems`` is the server registry (#3385). It defaults to the offline
 * table, so every pre-#3385 call site keeps its exact behaviour and no caller
 * is obliged to thread it.
 */
export function resolveProvenanceSource(
  sourceId: string | null | undefined,
  docsById?: Map<string, EntitySourceDoc>,
  systems: ReadonlyArray<SourceSystemEntry> = DEFAULT_SOURCE_SYSTEMS,
): ResolvedProvenance {
  if (!sourceId || sourceId.trim() === '') return UNKNOWN;
  // Document first: a real linked document always wins its own preview path.
  const documentLabel = provenanceLabel(sourceId, docsById);
  if (documentLabel) return { kind: 'document', label: documentLabel, documentId: sourceId };
  for (const { match, label } of compiledFor(systems)) {
    if (match.test(sourceId)) return { kind: 'system', label, documentId: null };
  }
  return UNKNOWN;
}
